// How the filters of the product search read in Explore: the labels of the
// sections, of the values and of the ranges, and the active filters as the
// chips show them. Pure: no React.

import {
  FilterValue,
  ProductFilterName,
  ProductFilters,
  ProductFlatFilterName,
  ProductPlaceFilterName,
} from "../../types/productPlane";
import { ActiveFilter } from "../Filters/ActiveFilterChips";
import {
  EXPLORE_FILTER_NAMES,
  ExploreFilterState,
  ExploreRange,
  withExplorePick,
} from "./exploreState";
import { formatCode } from "./productFields";

// What the results are called in the count line and the search field
export const PRODUCT_NOUN = "products";

// What the empty search field says the keyword is matched against
export const PRODUCT_SEARCH_PLACEHOLDER =
  "Search name, other names, category, capability, maker, description";

// The label of the switch that lists the results that carry no name
export const unnamedLabel = (noun: string): string =>
  `${noun.charAt(0).toUpperCase()}${noun.slice(1)} without a name`;

// The label of each filter's section, and of its chips
export const EXPLORE_FILTER_LABELS: Record<ProductFilterName, string> = {
  category: "Category",
  capability: "Capability",
  mission: "Mission",
  condition: "Condition",
  mode: "Mode",
  level: "Level",
  status: "Status",
  maker: "Maker",
  maker_country: "Maker / developer country",
  operator: "Operator",
  operator_country: "Operator country",
  programme: "Programme",
};

// The country filters: their values are places, listed by group with flags
export const PLACE_FILTERS: readonly ProductPlaceFilterName[] = [
  "maker_country",
  "operator_country",
];

// The label of the section of the numeric ranges
export const SPECIFICATIONS_LABEL = "Specifications";

// The line under the conditions
export const ONE_CLAIM_NOTE =
  "Ability, condition and mode must be met by one and the same claim.";

// The long filters: each has a box whose text the backend shortens it by
export const NARROWED_FILTERS: readonly ProductFlatFilterName[] = [
  "maker",
  "maker_country",
  "operator",
  "operator_country",
  "programme",
];

// Most values of a flat filter the backend returns on request
export const ALL_FILTER_VALUES = 200;

// The text typed into the box of one long filter
export interface ExploreNarrow {
  filter: ProductFlatFilterName;
  text: string;
}

// How a filter value reads: its name, or, for a fixed code, the code as words
export const filterValueLabel = (
  value: Pick<FilterValue, "value" | "name">,
): string => value.name ?? formatCode(value.value);

// How the key of a specification reads: "max_speed" reads "Max speed"
export const specificationLabel = (key: string): string => {
  const words = formatCode(key);
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// How a range reads: "2 to 10 kg", "from 2 kg", "up to 10 kg"
export const rangeText = (range: ExploreRange, unit?: string): string => {
  const withUnit = (text: string) => (unit ? `${text} ${unit}` : text);
  if (range.minimum !== null && range.maximum !== null) {
    return withUnit(`${range.minimum} to ${range.maximum}`);
  }
  if (range.minimum !== null) {
    return withUnit(`from ${range.minimum}`);
  }
  if (range.maximum !== null) {
    return withUnit(`up to ${range.maximum}`);
  }
  return "any value";
};

// What the filters of the answers so far call the values and in which unit
// they state the specifications. A pick the latest answer does not list (a
// maker outside a shortened list) keeps the name an earlier answer gave it.
export interface ExploreFilterNames {
  // Per filter, the label of each value
  values: { [Name in ProductFilterName]?: Map<string, string> };
  // The unit of each specification, by key
  units: Map<string, string>;
}

export const noFilterNames = (): ExploreFilterNames => ({
  values: {},
  units: new Map(),
});

// Adds the names of one answer's filters to the names known
export const rememberFilterNames = (
  names: ExploreFilterNames,
  filters: ProductFilters,
): void => {
  const remember = (labels: Map<string, string>, values: FilterValue[]) => {
    for (const value of values) {
      labels.set(value.value, filterValueLabel(value));
      remember(labels, value.children);
    }
  };
  for (const filter of filters.choices) {
    const labels = names.values[filter.name] ?? new Map<string, string>();
    names.values[filter.name] = labels;
    remember(labels, filter.values);
  }
  for (const spec of filters.numeric) {
    names.units.set(spec.key, spec.unit);
  }
};

// An active filter of Explore: what its chip shows, and the change of the
// state that removes it
export interface ExploreActiveFilter extends ActiveFilter {
  remove: Partial<ExploreFilterState>;
}

// The active filters of a state, picks first in the order of the panel,
// then the ranges. A value no answer named yet is shown as it is.
export const exploreActiveFilters = (
  state: ExploreFilterState,
  names: ExploreFilterNames,
): ExploreActiveFilter[] => [
  ...EXPLORE_FILTER_NAMES.flatMap((name) =>
    state[name].map((value) => ({
      id: `${name}=${value}`,
      section: EXPLORE_FILTER_LABELS[name],
      value: names.values[name]?.get(value) ?? value,
      remove: { [name]: withExplorePick(state[name], value, false) },
    })),
  ),
  ...state.numeric.map((range) => ({
    id: `numeric=${range.key}`,
    section: specificationLabel(range.key),
    value: rangeText(range, names.units.get(range.key)),
    remove: { numeric: state.numeric.filter((other) => other !== range) },
  })),
];
